import React, { useMemo, type ComponentProps } from 'react';
import { Text, Image, Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { useCalculatorContext } from '../../contexts/CalculatorContext';
import iconInfo from './icon-info.svg';
import iconWarning from './icon-warning.svg';

const CALC_BANNER_STATUSES = {
    INFO: 'info',
    WARNING: 'warning',
};

const BANNERS_TYPE = {
    LEASE_BANNER: 'lease',
    CONTRACT_OF_MANDATE_BANNER: 'contract_of_mandate',
    NO_COPYRIGHT_TRANSFER_BANNER: 'no_copyright_transfer',
};

type CalculatorBannerProps = {
    status: keyof typeof CALC_BANNER_STATUSES;
} & ComponentProps<any>;

export const CalculatorBanners = () => {
    const {
        paymentDetails: { billingBannerType },
    } = useCalculatorContext();
    const [t] = useTranslation();
    const banners = useMemo(
        () => ({
            [BANNERS_TYPE.LEASE_BANNER]: {
                status: 'info',
                content: t('calculator.banners.lease'),
            },
            [BANNERS_TYPE.CONTRACT_OF_MANDATE_BANNER]: {
                status: 'info',
                content: t('calculator.banners.contract_of_mandate'),
            },
            [BANNERS_TYPE.NO_COPYRIGHT_TRANSFER_BANNER]: {
                status: 'info',
                content: t('calculator.banners.no_copyright_transfer'),
            },
        }),
        [t],
    );

    if (billingBannerType === undefined || billingBannerType === null) {
        return <></>;
    }

    const banner = banners[billingBannerType];
    if (banner === undefined) {
        return <></>;
    }

    return <CalculatorBanner status={banner.status}>{banner.content}</CalculatorBanner>;
};

const CalculatorBanner = React.memo(({ status, children }: CalculatorBannerProps) => {
    const iconStatus = status === CALC_BANNER_STATUSES.WARNING ? iconWarning : iconInfo;

    return (
        <BannerBox>
            <Image src={iconStatus} alt="" width="2.4rem" height="2.4rem" />
            <Box>
                <Text textStyle="small" color="general.darkBlue">
                    {children}
                </Text>
            </Box>
        </BannerBox>
    );
});

const BannerBox = styled(Box)`
    display: flex;
    gap: var(--ck-space-5);
    background: rgba(242, 201, 76, 0.1);
    border-radius: var(--ck-space-2);
    padding: var(--ck-space-4) var(--ck-space-6);
    margin: var(--ck-space-8) 0;
    align-items: center;
`;
